"use client"

import { useRef } from "react"
import { Icon } from "@/shared/ui/icon"
import { EARLY_ACCESS_LABEL } from "@lib/early-access"
import { AtlasInlineOrb } from "./AtlasThinkingOrb"
import { useAtlasReveal } from "./useAtlasReveal"

const onboardingRevealTargets = [
  { selector: ".onboarding-copy", y: 34 },
  { selector: ".onboarding-card", y: 42, scale: 0.98, stagger: 100 },
] as const

type OnboardingStep = {
  title: string
  text: string
  icon: string
}

const onboardingSteps: readonly OnboardingStep[] = [
  {
    title: "Расскажите о первой задаче.",
    text: `Кнопка «${EARLY_ACCESS_LABEL}» откроет короткую форму: опишите компанию и что Atlas должен сделать первым.`,
    icon: "login-3-bold-duotone",
  },
  {
    title: "Поставьте задачу обычными словами.",
    text: "Не выбирайте модель и не собирайте промпт. Напишите результат, который хотите получить.",
    icon: "user-speak-rounded-bold-duotone",
  },
  {
    title: "Подключите нужные сервисы, когда это понадобится.",
    text: "Доступы появляются только под конкретный сценарий: CRM, задачи, сообщения или аналитика.",
    icon: "plug-circle-bold-duotone",
  },
] as const

function OnboardingCard({ step, index }: { step: OnboardingStep; index: number }) {
  return (
    <article className="onboarding-card group min-h-72 overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 transition-colors duration-500 hover:border-slate-300 dark:border-white/10 dark:bg-white/[0.04] dark:hover:border-white/20 sm:p-6 lg:col-span-4">
      <div className="flex h-full flex-col justify-between">
        <div className="flex items-start justify-between gap-4">
          <span className="flex size-12 items-center justify-center rounded-xl bg-slate-100 text-slate-800 transition-transform duration-700 ease-out group-hover:scale-105 dark:bg-white/10 dark:text-white">
            <Icon name={step.icon} className="size-5" />
          </span>
          <span className="type-numeric text-xs text-brand-700 dark:text-jungle-300">
            {String(index + 1).padStart(2, "0")}
          </span>
        </div>

        <div className="my-6 flex flex-1 flex-col justify-start">
          <h3 className="text-title text-slate-950 dark:text-white">{step.title}</h3>
          <p className="mt-3 text-base text-slate-600 dark:text-white/60">{step.text}</p>
        </div>

        <div className="h-px w-full overflow-hidden bg-slate-200 dark:bg-white/10">
          <div className="h-full w-1/2 bg-brand-500 transition-transform duration-700 ease-out group-hover:translate-x-full dark:bg-jungle-400" />
        </div>
      </div>
    </article>
  )
}

/**
 * Как начинается работа с Atlas. Само действие живёт в общем EarlyAccessCta,
 * который страница ставит сразу после этой секции.
 */
export default function OnboardingConversionSection() {
  const sectionRef = useRef<HTMLElement>(null)

  useAtlasReveal(sectionRef, onboardingRevealTargets)

  return (
    <section
      ref={sectionRef}
      id="onboarding"
      aria-labelledby="onboarding-heading"
      className="relative overflow-x-hidden bg-white pt-24 dark:bg-gray-900 sm:pt-32 lg:pt-44"
    >
      <div className="pointer-events-none absolute left-1/2 top-20 size-[36rem] -translate-x-1/2 rounded-full bg-orange-500/10 blur-3xl dark:bg-jungle-400/25" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <div className="onboarding-copy max-w-6xl">
          <p className="type-eyebrow inline-flex items-center">
            <AtlasInlineOrb state="working" className="ml-0" />
            Начало работы
          </p>
          <h2
            id="onboarding-heading"
            className="mt-4 max-w-5xl text-headline text-slate-950 dark:text-white"
          >
            Первая задача — через несколько минут.
          </h2>
          <p className="mt-6 max-w-3xl text-lead text-slate-600 dark:text-white/60">
            Старт не похож на внедрение новой системы. Сначала вы описываете одну понятную
            задачу, а подключения появляются только там, где без них нельзя получить
            результат.
          </p>
        </div>

        <div className="onboarding-grid mt-12 grid grid-flow-dense gap-4 lg:grid-cols-12">
          {onboardingSteps.map((step, index) => (
            <OnboardingCard key={step.title} step={step} index={index} />
          ))}
        </div>
      </div>
    </section>
  )
}
